import React, { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import styled, { css } from "styled-components";

import HtmlHead from "components/atoms/HtmlHead";
import Icon from "components/atoms/Icon";
import { CountTagItem, TagItem, TagText } from "components/atoms/Tag";
import Tooltip from "components/atoms/Tooltip";
import Confirm from "components/atoms/alert/Confirm";
import Button, { BUTTON_STYLES } from "components/atoms/button";
import Checkbox from "components/atoms/input/Checkbox";
import SelectBox from "components/atoms/input/SelectBox";
import Text from "components/atoms/text/Text";
import useToast from "components/atoms/toast/useToast";

import { BodyWithBreadcrumb, Breadcrumb, BreadcrumbTitle } from "components/molecules/Breadcrumb";
import Table from "components/molecules/table/Table";
import { compareColumns, getColumnSize, getColumnVisibility } from "components/molecules/table/TableManager";
import NavigatorFolderEditModal from "components/page/modal/NavigatorFolderEditModal";
import NavigatorTagMultiAddModal from "components/page/modal/NavigatorTagMultiAddModal";
import ReferenceFileUploadModal from "components/page/modal/ReferenceFileUploadModal";
import LoginUserLayout from "components/templates/LoginUserLayout";

import {
    useNavigatorFolderResourcesCreate,
    useNavigatorFolders,
    useNavigatorResources,
    useNavigatorTagResourcesCreate,
    useNavigatorTags,
} from "hooks/queries/useNavigator";
import { useManuscriptBulkDelete } from "hooks/queries/useManuscript";
import { useNoteBulkDelete } from "hooks/queries/useNote";
import { useReferenceDelete } from "hooks/queries/useReference";
import { useStableQueryData } from "hooks/useStableQueryData";

import { genericEnterKeyDown } from "utils/validation";

import moment from "moment/moment";

const HeaderLeftWrap = styled.div`
    display: flex;
    align-items: center;
    flex: 1;
    gap: 8px;
    min-width: 0;
`;

const SelectedFilterWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    overflow-x: auto;
    white-space: nowrap;

    &::-webkit-scrollbar {
        display: none;
    }
`;

const HeaderCountTagItem = styled(CountTagItem)`
    flex-shrink: 0;
    margin: 0px 8px;
`;

const HeaderTitle = styled(BreadcrumbTitle)`
    flex-shrink: 0;
`;

const BodyHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 0;
`;

const BodyHeaderLeftSection = styled.div`
    display: flex;
    flex: 1;
    align-items: center;
    width: 171px;
    margin-left: 10px;
`;

const BodyHeaderRightSection = styled.div`
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: flex-end;
    margin: 0 0 0 24px;
`;

const SearchWrap = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 12px 0 0;
    width: 100%;
    min-width: 200px;
    max-width: 320px;
`;

const DivideLine = styled.div`
    width: 1px;
    height: 24px;
    margin: 0 12px 0 0;
    background-color: var(--color-Line);
`;

const SearchInputBox = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1px 4px;
    width: 100%;
    height: 28px;
    background-color: var(--color-White);
    border: solid 1px var(--color-Line);
    border-radius: 4px;

    &:focus-within {
        border: solid 1px var(--color-Key);
    }
`;

const SearchInput = styled.input`
    padding: 0 8px;
    width: calc(100% - 24px);
    height: 24px;
    font-size: 13px;
    font-weight: 400;
    color: var(--color-Black);
    background-color: transparent;
    border: transparent;
    outline: var(--color-Key);
`;

const SearchButtonWrap = styled.div`
    display: flex;
    align-items: center;
    height: 24px;
`;

const FavoriteWrap = styled.div`
    display: flex;
    align-items: center;
    margin-right: 12px;
`;

const EmptyView = styled.div`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    font-weight: 500;
    color: var(--color-SubBlack);
`;

const BodyContent = styled.div`
    flex-direction: column;
    align-items: center;
    width: 100%;
    height: 100%;
    overflow: auto;

    ${(props) =>
        props.$isFileDragging &&
        css`
            border: dashed 2px var(--color-Key);
            background-color: var(--color-Base1);
        `};
`;

const BodyContentUploadView = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    height: 100%;
`;

const UploadDropBox = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    border: dashed 2px var(--color-Key);
    background-color: var(--color-Base1);
`;

const TitleCell = styled.div`
    display: flex;
    align-items: center;
    min-width: 0;
`;

const HeaderTagItem = styled(TagItem)`
    flex-shrink: 0;
`;

const FolderSeparator = styled.div`
    display: flex;
    align-items: center;
    height: 20px;
`;

const RESOURCE_TYPE_META = {
    reference: {
        label: "참고문헌",
        icon: "sideBarReference",
    },
    note: {
        label: "연구노트",
        icon: "sideBarNote",
    },
    manuscript: {
        label: "논문원고",
        icon: "sideBarManuscript",
    },
};

const DEFAULT_PARAMS = {
    page_size: 10,
    page: 1,
    search: "",
    ordering: "-created_at",
    favorites: false,
};

const PAGE_KEY = "navigatorList";
const NAVIGATOR_TREE_ITEM_MIME_TYPE = "application/x-navigator-tree-item";
const COMPACT_COLUMN_MAX_SIZE = {
    id: 40,
    is_favorite: 40,
    resource_type: 56,
};

const getNavigatorColumnSize = (columnId, tableConfig, defaultSize) => {
    const storedSize = getColumnSize(columnId, tableConfig);
    if (!storedSize) {
        return defaultSize;
    }

    const compactMaxSize = COMPACT_COLUMN_MAX_SIZE[columnId];
    if (compactMaxSize && storedSize > compactMaxSize) {
        return defaultSize;
    }

    return storedSize;
};

const getInitialTableConfig = () => {
    try {
        return localStorage.getItem(PAGE_KEY) ? JSON.parse(localStorage.getItem(PAGE_KEY)).navigator || [] : [];
    } catch (error) {
        return [];
    }
};

const EMPTY_NAVIGATOR = {
    count: 0,
    results: [],
};

function formatBytes(bytes, decimals = 2) {
    if (!Number(bytes)) return "0 Bytes";

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

function getParams(searchParams) {
    return {
        page_size: Number(searchParams.get("page_size") || DEFAULT_PARAMS.page_size),
        page: Number(searchParams.get("page") || DEFAULT_PARAMS.page),
        search: searchParams.get("search") || DEFAULT_PARAMS.search,
        ordering: searchParams.get("ordering") || DEFAULT_PARAMS.ordering,
        favorites: searchParams.get("favorites") === "true",
        folder: searchParams.get("folder") || "",
        tags: searchParams.get("tags") || "",
    };
}

function NavigationPage() {
    const { space_uuid } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { setToast } = useToast();
    const [urlSearchParams, setUrlSearchParams] = useSearchParams();
    const searchParams = useMemo(() => getParams(urlSearchParams), [urlSearchParams]);
    const [searchText, setSearchText] = useState(searchParams.search);
    const [checkedResources, setCheckedResources] = useState([]);
    const [navigatorFolderModal, setNavigatorFolderModal] = useState(false);
    const [navigatorTagModal, setNavigatorTagModal] = useState(false);
    const [isFileDragging, setIsFileDragging] = useState(false);
    const [uploadingFiles, setUploadingFiles] = useState([]);
    const [showFileUploadModal, setShowFileUploadModal] = useState(false);
    const [tableConfig, setTableConfig] = useState(getInitialTableConfig);
    const navigatorQuery = useNavigatorResources(searchParams);
    const { data: navigatorResources, isLoading, isRefreshing } = useStableQueryData(navigatorQuery, EMPTY_NAVIGATOR);
    const folderQuery = useNavigatorFolders();
    const tagQuery = useNavigatorTags();
    const folderList = folderQuery.data || [];
    const tagList = tagQuery.data || [];
    const referenceDelete = useReferenceDelete();
    const noteBulkDelete = useNoteBulkDelete();
    const manuscriptBulkDelete = useManuscriptBulkDelete();
    const navigatorFolderResourcesCreate = useNavigatorFolderResourcesCreate();
    const navigatorTagResourcesCreate = useNavigatorTagResourcesCreate();

    const currentPageResources = useMemo(() => navigatorResources?.results || [], [navigatorResources?.results]);
    const currentPageResourceKeys = useMemo(
        () => currentPageResources.map((resource) => `${resource.resource_type}:${resource.id}`),
        [currentPageResources],
    );
    const checkedResourceKeys = useMemo(
        () => checkedResources.map((resource) => `${resource.resource_type}:${resource.id}`),
        [checkedResources],
    );
    const checkedResourceKeySet = useMemo(() => new Set(checkedResourceKeys), [checkedResourceKeys]);
    const isCurrentPageAllChecked =
        currentPageResourceKeys.length > 0 && currentPageResourceKeys.every((key) => checkedResourceKeySet.has(key));
    const isCurrentPagePartiallyChecked =
        currentPageResourceKeys.some((key) => checkedResourceKeySet.has(key)) && !isCurrentPageAllChecked;

    useEffect(() => {
        setSearchText(searchParams.search);
    }, [searchParams.search]);

    const updateSearchParams = (patch) => {
        const nextParams = {
            ...searchParams,
            ...patch,
        };
        const nextUrlSearchParams = new URLSearchParams();

        Object.entries(nextParams).forEach(([key, value]) => {
            if (value === undefined || value === null || value === "" || value === false) {
                return;
            }
            nextUrlSearchParams.set(key, String(value));
        });

        setUrlSearchParams(nextUrlSearchParams);
    };

    const toggleResourceChecked = (resource, event) => {
        const checked = event.target.checked;
        const resourceKey = `${resource.resource_type}:${resource.id}`;
        setCheckedResources((prev) => {
            const next = prev.filter((item) => `${item.resource_type}:${item.id}` !== resourceKey);
            if (checked) {
                next.push({
                    resource_type: resource.resource_type,
                    id: resource.id,
                });
            }
            return next;
        });
    };

    const handleCheckboxAllChange = (event) => {
        const checked = event.target.checked;
        setCheckedResources((prev) => {
            const currentPageKeySet = new Set(currentPageResourceKeys);
            const rest = prev.filter((resource) => !currentPageKeySet.has(`${resource.resource_type}:${resource.id}`));
            if (!checked) {
                return rest;
            }
            return [
                ...rest,
                ...currentPageResources.map((resource) => ({
                    resource_type: resource.resource_type,
                    id: resource.id,
                })),
            ];
        });
    };

    const getSelectedIdsByType = (resourceType) =>
        checkedResources.filter((resource) => resource.resource_type === resourceType).map((resource) => resource.id);

    const handleDeleteSelectedResources = () => {
        Confirm("warn", "선택 항목 삭제", "선택한 항목을 삭제하시겠습니까?", "삭제", async () => {
            const referenceIds = getSelectedIdsByType("reference");
            const noteIds = getSelectedIdsByType("note");
            const manuscriptIds = getSelectedIdsByType("manuscript");

            try {
                const requests = [];
                if (referenceIds.length > 0) requests.push(referenceDelete.mutateAsync({ ids: referenceIds }));
                if (noteIds.length > 0) requests.push(noteBulkDelete.mutateAsync({ ids: noteIds }));
                if (manuscriptIds.length > 0) {
                    requests.push(manuscriptBulkDelete.mutateAsync({ ids: manuscriptIds }));
                }
                await Promise.all(requests);
                setCheckedResources([]);
                queryClient.invalidateQueries({ queryKey: "navigatorResources" });
                queryClient.invalidateQueries({ queryKey: "navigatorFolders" });
                queryClient.invalidateQueries({ queryKey: "navigatorTags" });
                setToast("선택한 항목이 삭제되었습니다.", "info");
            } catch (error) {
                setToast("삭제에 실패했습니다. 잠시후 시도해주세요.", "error");
            }
        });
    };

    const handleExecuteCheckedResourcesAction = (value) => {
        if (checkedResources.length === 0) {
            setToast("선택된 항목이 없습니다.", "warn");
            return;
        }

        if (value === "delete") {
            handleDeleteSelectedResources();
            return;
        }

        if (value === "folder") {
            setNavigatorFolderModal(true);
            return;
        }

        if (value === "tag") {
            setNavigatorTagModal(true);
        }
    };

    const handleSearch = (search) => {
        updateSearchParams({
            search,
            page: 1,
        });
    };

    const handlePageChange = (page) => {
        updateSearchParams({ page });
    };

    const handlePageSizeChange = (pageSize) => {
        updateSearchParams({
            page_size: pageSize,
            page: 1,
        });
    };

    const handleSortChange = (updatedSorting) => {
        const sorting = updatedSorting?.[0];
        updateSearchParams({
            ordering: sorting ? `${sorting.desc ? "-" : ""}${sorting.id}` : "",
            page: 1,
        });
    };

    const handleFileDragEnter = (event) => {
        event.preventDefault();
        event.stopPropagation();
        const types = Array.from(event.dataTransfer.types);
        if (types.includes("Files")) {
            setIsFileDragging(true);
        }
    };

    const handleFileDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
        const types = Array.from(event.dataTransfer.types);
        if (types.includes("Files")) {
            setIsFileDragging(true);
        }
    };

    const handleFileDragLeave = (event) => {
        event.preventDefault();
        event.stopPropagation();
        const types = Array.from(event.dataTransfer.types);
        if (types.includes("Files")) {
            setIsFileDragging(false);
        }
    };

    const handleFileDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        const types = Array.from(event.dataTransfer.types);
        if (types.includes("Files")) {
            setIsFileDragging(false);
            if (event.dataTransfer.files) {
                setUploadingFiles(Array.from(event.dataTransfer.files));
                setShowFileUploadModal(true);
            }
        }
    };

    const findParentFolder = (folderId, path = []) => {
        const folder = folderList.find((item) => item.id === folderId);
        if (folder) {
            path.unshift(folder);
            if (folder.parent) {
                return findParentFolder(folder.parent, path);
            }
        }
        return path;
    };

    const selectedParamsRenderer = () => {
        if (searchParams.tags && searchParams.tags.length > 0) {
            const selectedTags = searchParams.tags
                .split(",")
                .filter((tag) => tag !== "")
                .map((tag) => tagList.find((item) => item.id === Number(tag)));

            const tagEls = selectedTags.map((tag, index) => (
                <HeaderTagItem key={index}>
                    <Icon name={"tag"} size={"8"} color={"var(--color-SubBlack)"} />
                    <TagText style={{ margin: "0 8px 0 8px" }}>{tag?.name || ""}</TagText>
                    <Icon
                        name={"x"}
                        size={"8"}
                        color={"var(--color-SubBlack)"}
                        aria-label={`${tag?.name || "태그"} 필터 제거`}
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                            if (!tag) return;
                            updateSearchParams({
                                tags: searchParams.tags
                                    .split(",")
                                    .filter((item) => Number(item) !== tag.id)
                                    .join(","),
                                page: 1,
                            });
                        }}
                    />
                </HeaderTagItem>
            ));

            if (selectedTags.length > 1) {
                tagEls.push(
                    <HeaderTagItem key={selectedTags.length}>
                        <TagText
                            aria-label={"태그 필터 전체 제거"}
                            style={{ cursor: "pointer" }}
                            onClick={() => updateSearchParams({ tags: "", page: 1 })}
                        >
                            Clear All
                        </TagText>
                    </HeaderTagItem>,
                );
            }

            return tagEls;
        }

        if (searchParams.folder) {
            const folderPath = findParentFolder(Number(searchParams.folder));

            if (folderPath.length === 0) return [];

            return [
                <HeaderTagItem key="all">
                    <TagText
                        aria-label={"폴더 필터 전체 해제"}
                        style={{ cursor: "pointer" }}
                        onClick={() => updateSearchParams({ folder: "", page: 1 })}
                    >
                        모든 연구자료
                    </TagText>
                </HeaderTagItem>,
                ...folderPath.map((folder, index) => (
                    <React.Fragment key={index}>
                        <FolderSeparator>
                            <Icon name="arrowRight" size="8" color="var(--color-SubBlack)" />
                        </FolderSeparator>
                        <HeaderTagItem>
                            <Icon name="folder" size="8" color="var(--color-SubBlack)" />
                            <TagText
                                style={{ cursor: "pointer", maxWidth: "150px", margin: "0 0 0 8px" }}
                                onClick={() => updateSearchParams({ folder: folder.id, page: 1 })}
                            >
                                {folder.name}
                            </TagText>
                        </HeaderTagItem>
                    </React.Fragment>
                )),
            ];
        }

        return [];
    };

    const handleRowDragStart = (event, row) => {
        const rowResource = {
            resource_type: row.original.resource_type,
            id: row.original.id,
        };
        const rowResourceKey = `${rowResource.resource_type}:${rowResource.id}`;
        const dragResources = checkedResourceKeySet.has(rowResourceKey) ? checkedResources : [rowResource];

        if (!checkedResourceKeySet.has(rowResourceKey)) {
            setCheckedResources((prev) => [
                ...prev.filter((resource) => `${resource.resource_type}:${resource.id}` !== rowResourceKey),
                rowResource,
            ]);
        }

        event.dataTransfer.setData(
            "application/json",
            JSON.stringify({
                resources: dragResources,
            }),
        );

        const dragPreview = document.createElement("div");
        dragPreview.style.position = "absolute";
        dragPreview.style.top = "-1000px";
        dragPreview.style.left = "-1000px";
        dragPreview.style.padding = "8px 12px";
        dragPreview.style.backgroundColor = "rgba(0, 0, 0, 0.7)";
        dragPreview.style.color = "#fff";
        dragPreview.style.fontSize = "14px";
        dragPreview.style.borderRadius = "4px";
        dragPreview.innerText = `${dragResources.length}개 요소를 폴더/태그로 이동`;

        document.body.appendChild(dragPreview);
        event.dataTransfer.setDragImage(dragPreview, 0, 0);

        setTimeout(() => {
            document.body.removeChild(dragPreview);
        }, 0);

        event.dataTransfer.effectAllowed = "move";
    };

    const handleRowDrop = (event, row) => {
        const data = event.dataTransfer.getData(NAVIGATOR_TREE_ITEM_MIME_TYPE);
        if (!data) return false;

        let navigatorItem;
        try {
            navigatorItem = JSON.parse(data);
        } catch (error) {
            return false;
        }

        const resource = {
            resource_type: row.original.resource_type,
            id: row.original.id,
        };

        if (navigatorItem.type === "folder") {
            navigatorFolderResourcesCreate.mutate(
                {
                    folder_id: navigatorItem.id,
                    resources: [resource],
                },
                {
                    onSuccess: () => setToast("선택한 폴더로 이동되었습니다.", "info"),
                    onError: () => setToast("폴더 연결에 실패했습니다.", "error"),
                },
            );
            return true;
        }

        if (navigatorItem.type === "tag") {
            navigatorTagResourcesCreate.mutate(
                {
                    tag_ids: [navigatorItem.id],
                    resources: [resource],
                    recommended_tags: [],
                    replace: false,
                },
                {
                    onSuccess: () => setToast("선택한 태그가 적용되었습니다.", "info"),
                    onError: () => setToast("태그 연결에 실패했습니다.", "error"),
                },
            );
            return true;
        }

        return false;
    };

    const columns = useMemo(
        () => [
            {
                id: "id",
                accessorKey: "id",
                name: "",
                size: getNavigatorColumnSize("id", tableConfig, 40),
                defaultVisibility: true,
                enableResizing: false,
                content: (info) => {
                    const resource = info.row.original;
                    const resourceKey = `${resource.resource_type}:${resource.id}`;
                    return (
                        <Checkbox
                            checked={checkedResourceKeySet.has(resourceKey)}
                            onChange={(checked) => toggleResourceChecked(resource, checked)}
                        />
                    );
                },
            },
            {
                id: "is_favorite",
                accessorKey: "is_favorite",
                name: "",
                size: getNavigatorColumnSize("is_favorite", tableConfig, 40),
                defaultVisibility: true,
                enableResizing: false,
                content: (info) =>
                    info.getValue() ? (
                        <Icon name={"bookmark"} color={"var(--color-Key)"} size={"16px"} />
                    ) : (
                        <Icon name={"bookmarkDisactive"} size={"16px"} />
                    ),
            },
            {
                id: "resource_type",
                accessorKey: "resource_type",
                name: "구분",
                size: getNavigatorColumnSize("resource_type", tableConfig, 56),
                defaultVisibility: true,
                enableResizing: false,
                content: (info) => {
                    const meta = RESOURCE_TYPE_META[info.getValue()] || {
                        label: info.getValue(),
                        icon: "sideBarReference",
                    };
                    return (
                        <Tooltip message={meta.label}>
                            <Icon name={meta.icon} size={"16px"} />
                        </Tooltip>
                    );
                },
            },
            {
                id: "display_name",
                accessorKey: "display_name",
                name: "제목",
                size: getColumnSize("display_name", tableConfig) || 420,
                defaultVisibility: true,
                content: (info) => {
                    return (
                        <TitleCell>
                            <Text variant={"body-md-400"} ellipsis>
                                {info.getValue()}
                            </Text>
                        </TitleCell>
                    );
                },
            },
            {
                id: "used_volume",
                accessorKey: "used_volume",
                name: "용량",
                size: getColumnSize("used_volume", tableConfig) || 110,
                defaultVisibility: true,
                text: (info) => formatBytes(info.getValue()),
            },
            {
                id: "created_by",
                accessorKey: "created_by",
                name: "생성자",
                size: getColumnSize("created_by", tableConfig) || 180,
                defaultVisibility: true,
                text: (info) => info.getValue()?.name || info.getValue()?.email || "",
            },
            {
                id: "created_at",
                accessorKey: "created_at",
                name: "생성일시",
                size: getColumnSize("created_at", tableConfig) || 160,
                defaultVisibility: true,
                text: (info) => (info.getValue() ? moment(info.getValue()).format("YYYY-MM-DD HH:mm:ss") : ""),
            },
        ],
        [checkedResourceKeySet, tableConfig],
    );

    useEffect(() => {
        const nextConfig = compareColumns(columns, [...tableConfig]);
        const hasConfigChanged = JSON.stringify(nextConfig) !== JSON.stringify(tableConfig);

        if (hasConfigChanged) {
            setTableConfig(nextConfig);
            return;
        }

        localStorage.setItem(
            PAGE_KEY,
            JSON.stringify({
                navigator: tableConfig,
            }),
        );
    }, [columns, tableConfig]);

    const renderBodyHeader = () => (
        <BodyHeader>
            <BodyHeaderLeftSection>
                <Checkbox
                    margin={"0 20px 0 0"}
                    indeterminate={isCurrentPagePartiallyChecked}
                    checked={isCurrentPageAllChecked}
                    onChange={handleCheckboxAllChange}
                />
                <SelectBox
                    width={"auto"}
                    height={"28px"}
                    optionWidth={"280px"}
                    optionAlign={"left"}
                    optionMaxHeight={"auto"}
                    items={[
                        { id: "", name: "--- 액션을 선택하세요. ---" },
                        { id: "delete", name: "선택된 항목을 삭제합니다." },
                        { id: "folder", name: "선택된 항목을 다른 폴더로 이동합니다." },
                        { id: "tag", name: "선택된 항목에 다른 태그를 추가합니다." },
                    ]}
                    textAlign={"left"}
                    value={""}
                    onChange={handleExecuteCheckedResourcesAction}
                />
                {checkedResources.length > 0 && (
                    <Text margin="0 0 0 10px" variant="body-sm-400" fontWeight="300">
                        {checkedResources.length}개의 항목이 선택됨
                    </Text>
                )}
            </BodyHeaderLeftSection>
            <BodyHeaderRightSection>
                <SearchWrap>
                    <SearchInputBox>
                        <SearchInput
                            value={searchText}
                            onChange={(event) => setSearchText(event.target.value)}
                            onKeyDown={(event) => genericEnterKeyDown(event, () => handleSearch(searchText))}
                        />
                        <SearchButtonWrap>
                            {searchText && (
                                <Tooltip message={"초기화"}>
                                    <Button
                                        {...BUTTON_STYLES.iconSurface}
                                        onClick={() => {
                                            setSearchText("");
                                            handleSearch("");
                                        }}
                                    >
                                        <Icon name={"x"} color={"var(--color-SubBlack)"} />
                                    </Button>
                                </Tooltip>
                            )}
                            <Tooltip message={"검색"}>
                                <Button {...BUTTON_STYLES.iconSurface} onClick={() => handleSearch(searchText)}>
                                    <Icon name={"search"} size={"12"} color={"var(--color-SubBlack)"} />
                                </Button>
                            </Tooltip>
                        </SearchButtonWrap>
                    </SearchInputBox>
                </SearchWrap>
                <FavoriteWrap>
                    <Tooltip message={"즐겨찾기만 보기"}>
                        <Button
                            {...BUTTON_STYLES.iconSurfaceStatic}
                            aria-label={"즐겨찾기만 보기"}
                            buttonSize={"24px"}
                            border={
                                searchParams.favorites
                                    ? "solid 2px var(--color-Key)"
                                    : "solid 2px var(--color-DisabledButton)"
                            }
                            onClick={() =>
                                updateSearchParams({
                                    favorites: !searchParams.favorites,
                                    page: 1,
                                })
                            }
                        >
                            <Icon
                                name={"bookmark"}
                                size={"12px"}
                                color={searchParams.favorites ? "var(--color-Key)" : "var(--color-DisabledButton)"}
                            />
                        </Button>
                    </Tooltip>
                </FavoriteWrap>
                <DivideLine />
            </BodyHeaderRightSection>
        </BodyHeader>
    );

    return (
        <LoginUserLayout searchParams={searchParams}>
            <HtmlHead title={"분류별 보기"} />
            <Breadcrumb>
                <HeaderLeftWrap>
                    <HeaderTitle>분류별 보기</HeaderTitle>
                    <SelectedFilterWrap>{selectedParamsRenderer()}</SelectedFilterWrap>
                    <HeaderCountTagItem>
                        <Text variant="body-sm-500" color={"#61766F"}>
                            {navigatorResources?.count || 0}건
                        </Text>
                    </HeaderCountTagItem>
                </HeaderLeftWrap>
            </Breadcrumb>
            <BodyWithBreadcrumb>
                {isLoading ? null : navigatorResources?.count !== 0 ? (
                    <>
                        {renderBodyHeader()}
                        <BodyContent
                            onDragEnter={handleFileDragEnter}
                            onDragOver={handleFileDragOver}
                            onDragLeave={handleFileDragLeave}
                            onDrop={handleFileDrop}
                            $isFileDragging={isFileDragging}
                        >
                            {isFileDragging ? (
                                <BodyContentUploadView>
                                    <UploadDropBox>
                                        <Icon name={"fileUploadOn"} size={"100px"} />
                                        <Text margin="24px 0 0 0" variant="body-lg-400" textAlign="center">
                                            현재 목록에 <strong>참고문헌 파일</strong>을 업로드합니다.
                                        </Text>
                                    </UploadDropBox>
                                </BodyContentUploadView>
                            ) : (
                                <Table
                                    data={navigatorResources?.results || []}
                                    columns={columns}
                                    useSizingColumns={true}
                                    onSizeChange={(changedColumn) => {
                                        Object.keys(changedColumn).forEach((key) => {
                                            setTableConfig((prev) =>
                                                prev.map((item) =>
                                                    item.id === key ? { ...item, size: changedColumn[key] } : item,
                                                ),
                                            );
                                        });
                                    }}
                                    useSortingColumns={true}
                                    columnVisibility={getColumnVisibility(tableConfig)}
                                    pagination={{
                                        pageIndex: searchParams.page,
                                        pageSize: searchParams.page_size,
                                        count: navigatorResources?.count,
                                    }}
                                    setPagination={(page) => {
                                        if (Number(searchParams.page_size) !== Number(page.pageSize)) {
                                            handlePageSizeChange(page.pageSize);
                                        } else if (searchParams.page !== Number(page.pageIndex)) {
                                            handlePageChange(page.pageIndex);
                                        }
                                    }}
                                    sorting={
                                        searchParams?.ordering
                                            ? [
                                                  {
                                                      id: searchParams.ordering.replace("-", ""),
                                                      desc: searchParams.ordering.startsWith("-"),
                                                  },
                                              ]
                                            : []
                                    }
                                    setSorting={handleSortChange}
                                    onRowClick={(row) => {
                                        if (!row.original.detail_path) return;
                                        navigate(`/${space_uuid}/${row.original.detail_path}`, {
                                            state: { from: `/${space_uuid}/navigator${window.location.search}` },
                                        });
                                    }}
                                    onRowDragStart={handleRowDragStart}
                                    onRowDragOver={(event) =>
                                        Array.from(event.dataTransfer.types || []).includes(
                                            NAVIGATOR_TREE_ITEM_MIME_TYPE,
                                        )
                                    }
                                    onRowDrop={handleRowDrop}
                                    checkedRowIds={checkedResourceKeys}
                                    checkedRowKeyGetter={(row) => `${row.original?.resource_type}:${row.original?.id}`}
                                    isRefreshing={isRefreshing}
                                />
                            )}
                        </BodyContent>
                    </>
                ) : (
                    <>
                        {renderBodyHeader()}
                        <EmptyView
                            onDragEnter={handleFileDragEnter}
                            onDragOver={handleFileDragOver}
                            onDragLeave={handleFileDragLeave}
                            onDrop={handleFileDrop}
                        >
                            일치하는 리소스가 없습니다.
                        </EmptyView>
                    </>
                )}
            </BodyWithBreadcrumb>
            {navigatorFolderModal && (
                <NavigatorFolderEditModal
                    exitModal={() => setNavigatorFolderModal(false)}
                    resources={checkedResources}
                />
            )}
            {navigatorTagModal && (
                <NavigatorTagMultiAddModal exitModal={() => setNavigatorTagModal(false)} resources={checkedResources} />
            )}
            {showFileUploadModal && (
                <ReferenceFileUploadModal
                    uploadingFiles={uploadingFiles}
                    exitModal={() => {
                        setShowFileUploadModal(false);
                        navigatorQuery.refetch();
                    }}
                    searchParams={searchParams}
                />
            )}
        </LoginUserLayout>
    );
}

export default NavigationPage;
